import type { ParsedReservationError } from '../../utils/generationRunContext';
import {
  buildManuscriptCreditPaywallCopy,
  buildManuscriptCreditRequiredFeedMessage,
  isInsufficientCreditReservationError,
  isTechnicalReservationFailure,
  logManuscriptCreditRequiredDiagnostic,
  resolveRecommendedManuscriptTier,
  resolveReservationRejectionOutcome,
  type PaywallModalState,
} from '../../utils/manuscriptCreditRouting.service';
import { ManuscriptEntitlementError } from '../adapters/ManuscriptEntitlementAdapter';

export type CompatCreditRoutingResult =
  | { action: 'proceed' }
  | { action: 'open_paywall'; paywall: PaywallModalState; message: string }
  | { action: 'technical_failure'; message: string }
  | { action: 'paused_recovery'; message: string };

export function routeCompatReservationError(input: {
  error: unknown;
  projectId: string;
  chapterNumber: number;
  targetChapterCount: number;
  manuscriptCreditBalances?: Record<string, number>;
}): CompatCreditRoutingResult {
  const parsed = normalizeReservationError(input.error);
  if (parsed instanceof ManuscriptEntitlementError) {
    const reason = parsed.code.toLowerCase();
    if (reason === 'no_manuscript_entitlement' || reason === 'insufficient_credits') {
      return buildPaywallRoute(input.projectId, input.chapterNumber, input.targetChapterCount, parsed.message);
    }
    if (reason === 'invalid_operation') {
      return { action: 'technical_failure', message: parsed.message };
    }
  }

  const outcome = resolveReservationRejectionOutcome({
    error: parsed,
    projectId: input.projectId,
    chapterNumber: input.chapterNumber,
    targetChapterCount: input.targetChapterCount,
    manuscriptCreditBalances: input.manuscriptCreditBalances as any,
  });

  if (outcome.openPaywall && outcome.paywallState) {
    if (outcome.diagnostic) {
      logManuscriptCreditRequiredDiagnostic(outcome.diagnostic);
    }
    return {
      action: 'open_paywall',
      paywall: outcome.paywallState,
      message: outcome.creatorFeedMessage,
    };
  }

  if (outcome.technicalFailure || isTechnicalReservationFailure(parsed)) {
    return {
      action: 'technical_failure',
      message: outcome.creatorFeedMessage || 'Generation paused. Please retry when ready.',
    };
  }

  if (isInsufficientCreditReservationError(parsed)) {
    return buildPaywallRoute(input.projectId, input.chapterNumber, input.targetChapterCount, parsed.message);
  }

  return { action: 'paused_recovery', message: parsed.message || 'Generation paused. Please retry when ready.' };
}

function buildPaywallRoute(
  projectId: string,
  chapterNumber: number,
  targetChapterCount: number,
  message: string,
): CompatCreditRoutingResult {
  const tier = resolveRecommendedManuscriptTier(targetChapterCount);
  const copy = buildManuscriptCreditPaywallCopy(tier);
  return {
    action: 'open_paywall',
    paywall: {
      isOpen: true,
      reason: 'generation',
      highlightedTier: tier,
      headline: copy.headline,
      subheadline: copy.subheadline,
    },
    message: buildManuscriptCreditRequiredFeedMessage(chapterNumber) || message,
  };
}

function normalizeReservationError(error: unknown): ParsedReservationError {
  if (error instanceof ManuscriptEntitlementError) {
    const code = error.code.toLowerCase();
    if (code === 'no_manuscript_entitlement') {
      return { kind: 'no_entitlement', message: error.message, reason: code };
    }
    if (code === 'insufficient_credits') {
      return { kind: 'insufficient_credits', message: error.message, reason: code };
    }
    if (code === 'invalid_operation') {
      return { kind: 'invalid_state', message: error.message, reason: code };
    }
    return { kind: 'unknown', message: error.message, reason: code };
  }
  const err = error as { status?: number; message?: string; code?: string };
  if (err.status === 502 || err.status === 503 || err.status === 504) {
    return { kind: 'unknown', message: err.message || 'Gateway error', reason: 'gateway_error', httpStatus: err.status };
  }
  return { kind: 'unknown', message: err?.message || 'Reservation failed', reason: err?.code };
}
